import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/content";
import { scrollTo } from "./Reveal";
import { Menu, X, Lock } from "lucide-react";

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["#about", t.nav.about],
    ["#manifesto", t.nav.manifesto],
    ["#sportello", t.nav.sportello],
    ["#projects", t.nav.projects],
    ["#events", t.nav.events],
    ["#news", t.nav.news],
    ["#roadmap", t.nav.roadmap],
  ];

  const go = (id) => { setOpen(false); scrollTo(id); };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-[68px] flex items-center justify-between">
        <button data-testid="logo" onClick={() => go("#top")} className="flex items-center gap-2.5">
          <img src={IMAGES.logo} alt="AGE Rende APS" className="h-11 w-11 rounded-full object-cover ring-1 ring-black/5" />
          <span className="hidden sm:block font-display font-extrabold text-lg tracking-tight leading-none">
            AGE Rende<span className="text-accent">.</span>
            <span className="block text-[10px] font-medium tracking-[0.2em] text-muted-foreground uppercase">Associazione Genitori</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-7">
          {links.map(([id, label]) => (
            <button
              key={id}
              data-testid={`nav-${id.slice(1)}`}
              onClick={() => go(id)}
              className="link-underline text-sm font-medium text-foreground/80 hover:text-foreground"
            >
              {label}
            </button>
          ))}
          <Link data-testid="nav-gallery" to="/galleria" className="link-underline text-sm font-medium text-foreground/80 hover:text-foreground">
            {t.nav.gallery}
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="text-xs font-semibold border border-foreground/20 rounded-full px-3 py-1.5 hover:bg-foreground hover:text-background transition-colors"
          >
            {lang === "it" ? "EN" : "IT"}
          </button>
          <Link
            data-testid="nav-area-soci"
            to="/area-soci"
            title="Area Soci"
            className="hidden sm:grid place-items-center w-9 h-9 rounded-full border border-foreground/20 hover:bg-foreground hover:text-background transition-colors"
          >
            <Lock size={15} />
          </Link>
          <button
            data-testid="nav-join-desktop"
            onClick={() => go("#join")}
            className="hidden sm:inline-flex bg-primary text-primary-foreground text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-[hsl(214,76%,24%)] active:scale-95 transition-all"
          >
            {t.nav.join}
          </button>
          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2" onClick={() => setOpen((o) => !o)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-b border-black/5 px-5 py-6 flex flex-col gap-4">
          {links.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} className="text-left text-lg font-medium">
              {label}
            </button>
          ))}
          <Link to="/galleria" onClick={() => setOpen(false)} className="text-left text-lg font-medium">
            {t.nav.gallery}
          </Link>
          <button onClick={() => go("#join")} className="mt-2 bg-primary text-primary-foreground font-semibold rounded-full px-5 py-3">
            {t.nav.join}
          </button>
        </div>
      )}
    </header>
  );
};
